/**
 * Heatmap de carga por pessoa × semanas (Salto A da gestão de capacidade).
 *
 * Cada célula = uma semana ISO. Cor:
 *   verde  ≤ 80%  (ok)
 *   amarelo 80-100%  (atenção)
 *   laranja 100-130% (alerta)
 *   vermelho >130% (crítico) ou pessoa em férias com fases
 *   cinza  0% (sem alocação)
 *
 * Click numa célula → modal com as fases envolvidas naquela semana.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Users, AlertTriangle, ArrowRight, X } from 'lucide-react';
import { dashboardAPI, fasesAPI } from '../../services/inovacaoAPI';

const BRAND = 'var(--brand, #20a490)';

const COR_SEV = {
  ok:        { bg: '#27ae60', label: 'OK' },
  'atenção': { bg: '#f1c40f', label: 'Atenção' },
  alerta:    { bg: '#e67e22', label: 'Alerta' },
  'crítico': { bg: '#e74c3c', label: 'Crítico' },
};

export default function CargaPessoas() {
  const [dados, setDados] = useState(null);
  const [dias, setDias] = useState(60);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {pessoa, semana}

  useEffect(() => {
    setLoading(true);
    dashboardAPI.cargaPessoas(dias)
      .then(setDados)
      .catch((e) => toast.error(e.response?.data?.detail || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [dias]);

  const semanasCabecalho = useMemo(() => {
    if (!dados?.pessoas?.length) return [];
    return dados.pessoas[0].semanas.map((s) => s.inicio);
  }, [dados]);

  const stats = useMemo(() => {
    if (!dados) return { criticos: 0, alertas: 0, atencoes: 0, oks: 0 };
    let c = 0, a = 0, at = 0, ok = 0;
    dados.pessoas.forEach((p) => p.semanas.forEach((s) => {
      if (s.severidade === 'crítico') c++;
      else if (s.severidade === 'alerta') a++;
      else if (s.severidade === 'atenção') at++;
      else ok++;
    }));
    return { criticos: c, alertas: a, atencoes: at, oks: ok };
  }, [dados]);

  return (
    <div style={{ padding: 32, background: '#f5f7fa', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                    marginBottom: 20, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                        color: BRAND, fontSize: '0.85rem', fontWeight: 600 }}>
            <Users size={14} /> CAPACIDADE & ALOCAÇÃO
          </div>
          <h1 style={{ margin: '8px 0 4px', color: '#2c3e50' }}>Carga das pessoas</h1>
          <p style={{ margin: 0, color: '#7f8c8d' }}>
            Heatmap de horas planejadas × capacidade semanal. Click numa célula
            pra ver as fases envolvidas.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ color: '#555', fontSize: '0.85rem' }}>
            Janela:&nbsp;
            <select value={dias} onChange={(e) => setDias(parseInt(e.target.value))}
                    style={{ padding: 6, borderRadius: 4, border: '1px solid #ddd' }}>
              <option value={30}>30 dias</option>
              <option value={60}>60 dias</option>
              <option value={90}>90 dias</option>
              <option value={120}>120 dias</option>
            </select>
          </label>
        </div>
      </div>

      {/* Stats rápidos */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                    gap: 12, marginBottom: 20 }}>
        <CardSev label="Críticos" valor={stats.criticos} cor="#e74c3c" />
        <CardSev label="Em alerta" valor={stats.alertas} cor="#e67e22" />
        <CardSev label="Atenção" valor={stats.atencoes} cor="#f1c40f" />
        <CardSev label="OK" valor={stats.oks} cor="#27ae60" />
      </div>

      {loading && <p style={{ color: '#7f8c8d' }}>Carregando…</p>}

      {!loading && (!dados || dados.pessoas.length === 0) && (
        <div style={cardStyle}>
          <p style={{ color: '#bdc3c7', textAlign: 'center', padding: 40, margin: 0 }}>
            Nenhuma pessoa com fases ativas nos próximos {dias} dias.
          </p>
        </div>
      )}

      {!loading && dados?.pessoas?.length > 0 && (
        <div style={{ ...cardStyle, padding: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0,
                          minWidth: 900 }}>
            <thead style={{ background: '#f8f9fa' }}>
              <tr>
                <th style={{ ...thStyle, position: 'sticky', left: 0, zIndex: 2,
                             background: '#f8f9fa', minWidth: 200 }}>
                  Pessoa
                </th>
                {semanasCabecalho.map((iso) => {
                  const d = new Date(iso);
                  return (
                    <th key={iso} style={{ ...thStyle, textAlign: 'center', minWidth: 56 }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                        {d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                      </div>
                      <div style={{ fontSize: '0.65rem', color: '#95a5a6', fontWeight: 400 }}>
                        sem {numeroSemana(d)}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {dados.pessoas.map((p) => (
                <tr key={p.usuario_id} style={{ borderTop: '1px solid #ecf0f1' }}>
                  <td style={{ ...tdStyle, position: 'sticky', left: 0, background: '#fff',
                               fontWeight: 500, zIndex: 1 }}>
                    {p.usuario_nome}
                  </td>
                  {p.semanas.map((s, i) => (
                    <td key={i} style={{ padding: 2 }}>
                      <Celula sem={s} onClick={() => setModal({ pessoa: p, semana: s })} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Legenda */}
      <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap',
                    fontSize: '0.8rem', color: '#7f8c8d' }}>
        {Object.entries(COR_SEV).map(([k, v]) => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: v.bg }} />
            {v.label}
          </span>
        ))}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 14, height: 14, borderRadius: 3, background: '#ecf0f1' }} />
          sem alocação
        </span>
      </div>

      {modal && <ModalDetalhes modal={modal} onClose={() => setModal(null)} />}
    </div>
  );
}

// ============ Componentes ============

function Celula({ sem, onClick }) {
  const tem = sem.horas_alocadas > 0;
  const fundo = tem ? COR_SEV[sem.severidade]?.bg || '#bdc3c7' : '#ecf0f1';
  const cor = tem ? '#fff' : '#bdc3c7';
  return (
    <div onClick={tem ? onClick : undefined}
         title={`${sem.horas_alocadas}h / ${sem.capacidade}h (${sem.percentual}%)`}
         style={{
           background: fundo, color: cor, borderRadius: 4,
           padding: 4, textAlign: 'center', fontSize: '0.7rem',
           fontWeight: 600, cursor: tem ? 'pointer' : 'default',
           transition: 'transform 0.1s',
         }}
         onMouseEnter={(e) => tem && (e.currentTarget.style.transform = 'scale(1.08)')}
         onMouseLeave={(e) => tem && (e.currentTarget.style.transform = 'none')}>
      {tem ? `${sem.percentual >= 1000 ? '∞' : sem.percentual.toFixed(0)}%` : '—'}
    </div>
  );
}

function CardSev({ label, valor, cor }) {
  return (
    <div style={{ ...cardStyle, padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ background: `${cor}15`, color: cor, padding: 8, borderRadius: 8 }}>
        <AlertTriangle size={18} />
      </div>
      <div>
        <div style={{ fontSize: '0.72rem', color: '#95a5a6', textTransform: 'uppercase' }}>
          {label}
        </div>
        <div style={{ fontSize: '1.4rem', fontWeight: 700, color: cor }}>{valor}</div>
        <div style={{ fontSize: '0.7rem', color: '#7f8c8d' }}>semanas-pessoa</div>
      </div>
    </div>
  );
}

function ModalDetalhes({ modal, onClose }) {
  const { pessoa, semana } = modal;
  const [fases, setFases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all((semana.fases_ids || []).map((id) => fasesAPI.obter(id).catch(() => null)))
      .then((arr) => setFases(arr.filter(Boolean)))
      .finally(() => setLoading(false));
  }, [semana]);

  return (
    <div style={modalBack} onClick={onClose}>
      <div style={modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ margin: 0, color: '#2c3e50' }}>{pessoa.usuario_nome}</h3>
            <p style={{ margin: '4px 0 0', color: '#7f8c8d', fontSize: '0.85rem' }}>
              Semana de {new Date(semana.inicio).toLocaleDateString('pt-BR')} a {new Date(semana.fim).toLocaleDateString('pt-BR')}
            </p>
          </div>
          <button onClick={onClose}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={20} color="#95a5a6" />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12,
                      margin: '16px 0', padding: 12, background: '#f8f9fa', borderRadius: 6 }}>
          <Stat label="Alocado" valor={`${semana.horas_alocadas}h`} cor={COR_SEV[semana.severidade]?.bg} />
          <Stat label="Capacidade" valor={`${semana.capacidade}h`} cor="#7f8c8d" />
          <Stat label="Uso" valor={semana.percentual >= 1000 ? '∞' : `${semana.percentual.toFixed(0)}%`}
                cor={COR_SEV[semana.severidade]?.bg} />
        </div>

        <h4 style={{ margin: '16px 0 8px', color: '#2c3e50' }}>
          Fases envolvidas ({fases.length})
        </h4>
        {loading ? <p>Carregando…</p> : (
          <div style={{ display: 'grid', gap: 6 }}>
            {fases.map((f) => (
              <Link key={f.id} to={`/inovacao/projetos/${f.projeto_id}`}
                    style={{ display: 'flex', alignItems: 'center', padding: 10,
                             background: '#f8f9fa', borderRadius: 6, textDecoration: 'none',
                             color: 'inherit' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.9rem', color: '#2c3e50', fontWeight: 500 }}>
                    {f.nome}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#7f8c8d', marginTop: 2 }}>
                    {f.horas_estimadas}h estimadas · {f.data_inicio_planejada} → {f.data_fim_planejada}
                  </div>
                </div>
                <ArrowRight size={14} color={BRAND} />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, valor, cor }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '0.7rem', color: '#95a5a6',
                    textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: '1.2rem', fontWeight: 700, color: cor || '#2c3e50' }}>{valor}</div>
    </div>
  );
}

// ============ helpers ============

function numeroSemana(d) {
  const target = new Date(d.valueOf());
  const dayNr = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  return 1 + Math.round(((target - firstThursday) / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7);
}

const cardStyle = {
  background: '#fff', borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: 16,
};
const thStyle = { padding: '12px 8px', textAlign: 'left', fontSize: '0.8rem',
                  color: '#7f8c8d', fontWeight: 600,
                  borderBottom: '1px solid #ecf0f1', whiteSpace: 'nowrap' };
const tdStyle = { padding: '8px 12px', fontSize: '0.85rem', color: '#2c3e50' };
const modalBack = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', zIndex: 1000, padding: 16,
};
const modalBox = {
  background: '#fff', borderRadius: 12, padding: 24,
  maxWidth: 600, width: '100%', maxHeight: '85vh', overflow: 'auto',
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
};
